import React, { ReactNode } from 'react'
import { FeatureFlagDefault, FeatureFlagName } from '../../../_constants/featureFlags'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'

interface FeatureFlagProps {
  /** The feature flag to check */
  flagName: FeatureFlagName
  /** Content to render when the feature is enabled */
  children: ReactNode
  /** Content to render when the feature is disabled */
  fallback?: ReactNode
  /**
   * Whether to show content based on defaultValue while loading.
   * If false, shows nothing (returns null) while loading.
   */
  showDefaultWhileLoading?: boolean
}

/**
 * Component that conditionally renders children based on feature flag state from AWS AppConfig.
 *
 * Note: This component only evaluates the boolean 'enabled' property of the feature flag.
 * Other feature flag values are ignored.
 *
 * @example
 * // Show content based on environment default while loading (default behavior)
 * <FeatureFlag flagName={FeatureFlagName.SSO_LOGIN} fallback={<OldUI />}>
 *   <NewUI />
 * </FeatureFlag>
 *
 * @example
 * // Hide component completely while loading
 * <FeatureFlag flagName={FeatureFlagName.SSO_LOGIN} fallback={null} showDefaultWhileLoading={false}>
 *   <OptionalComponent />
 * </FeatureFlag>
 */
const FeatureFlag: React.FC<FeatureFlagProps> = ({
  flagName,
  children,
  fallback = null,
  showDefaultWhileLoading = true,
}) => {
  const defaultValue = FeatureFlagDefault[flagName]
  const featureFlagData = useFeatureFlag(flagName)

  // Handle loading state - when feature flag data is not yet available
  if (!featureFlagData) {
    return showDefaultWhileLoading ? <>{defaultValue ? children : fallback}</> : null
  }

  // Handle feature flag state
  return <>{featureFlagData.enabled ? children : fallback}</>
}

export default FeatureFlag
